import React, { useState } from 'react';
import { X, Bot, CheckCircle, Loader2 } from 'lucide-react';

const IntakeModal = ({ isOpen, onClose, type = 'client-zero' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Honeypot field for bot detection
  const [honeypot, setHoneypot] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const modalConfig = {
    'client-zero': {
      eyebrow: 'AVA Client Zero',
      title: 'Apply for Client Zero',
      subtitle: "Bring us one business problem — we'll determine whether AVA can help solve it.",
      messagePlaceholder: "Describe the repetitive work or workflow you'd like to automate…",
      messageLabel: "The business problem you'd like AVA to solve *",
      submitText: 'Submit Application',
      successTitle: 'Application Received',
      successMessage: 'Thank you for applying to the AVA Client Zero Program. Our team will follow up with you shortly.'
    },
    'meet-ava': {
      eyebrow: 'Meet AVA™',
      title: 'Learn More About AVA',
      subtitle: "Tell us about your business and we'll show you how AVA can help.",
      messagePlaceholder: "What repetitive work is consuming your team's time?",
      messageLabel: "What would you like AVA to help with? *",
      submitText: 'Request Information',
      successTitle: 'Request Received',
      successMessage: 'Thank you for your interest in AVA. Our team will reach out to you shortly with more information.'
    }
  };

  const config = modalConfig[type] || modalConfig['client-zero'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!formData.email.trim() || !formData.message.trim()) {
      setError('Please fill in your email and message.');
      return;
    }

    // Honeypot check
    if (honeypot) {
      setIsSuccess(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim() || 'Not provided',
          email: formData.email.trim(),
          company: formData.company.trim() || 'Not provided',
          phone: formData.phone.trim() || 'Not provided',
          service: type === 'client-zero' ? 'altam-demo' : 'ava-inquiry',
          message: formData.message.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      setIsSuccess(true);
    } catch (err) {
      setError('We couldn\'t submit your request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    setIsSuccess(false);
    setError('');
    setHoneypot('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0A1538]/60 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div 
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-8 max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>

        {isSuccess ? (
          /* Success State */
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-2xl font-bold text-[#0A1538] mb-3">{config.successTitle}</h3>
            <p className="text-gray-600 mb-6">{config.successMessage}</p>
            <button
              onClick={handleClose}
              className="btn btn-primary"
            >
              Done
            </button>
          </div>
        ) : (
          /* Form State */
          <>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Bot className="h-5 w-5 text-[#227AFF]" />
                <span className="text-xs font-bold text-[#227AFF] uppercase tracking-wider">
                  {config.eyebrow}
                </span>
              </div>
              <h3 className="text-2xl font-bold text-[#0A1538] mb-2">{config.title}</h3>
              <p className="text-gray-600 text-sm">{config.subtitle}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#227AFF] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Work email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@company.com"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#227AFF] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Company</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Company name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#227AFF] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Phone (optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 555 000 0000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#227AFF] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">{config.messageLabel}</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder={config.messagePlaceholder}
                  rows={4}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#227AFF] focus:border-transparent resize-none"
                />
              </div>

              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="absolute left-[-9999px] w-[1px] h-[1px] opacity-0"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              {error && (
                <div className="text-red-500 text-sm">{error}</div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn btn-primary py-4 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  config.submitText
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default IntakeModal;
